import { comoSeLlama, type Empresa } from "@/componentes/empresa/tipos"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"

/**
 * El membrete de la agencia **sobre papel**: la marca, la razón social, el descriptor
 * y el RIF, con el filete dorado de la firma corporativa debajo.
 *
 * ⚠ **No es `EncabezadoEmpresa`, y la diferencia tiene motivo.** Aquél es el membrete
 * de los documentos que se leen en PANTALLA —el manual, los documentos del SGI, la
 * previa de la configuración—: se mide en `rem`, pinta con la tinta del tema y enseña
 * el logo que alguien SUBIÓ en «Empresa». Esto es tinta sobre papel: se mide en
 * milímetros, para que crezca con la hoja, y dibuja la marca VECTORIAL. Hoy no hay
 * logo subido —`logo_ruta` está vacío en producción—, así que el membrete de pantalla
 * saldría en papel sin ninguna marca; y aunque lo hubiera, un PNG de 148 KB a tamaño
 * de carta se ve lo que se ve. Son dos membretes porque son dos materiales, no por
 * descuido: el texto sigue saliendo de la misma configuración de empresa, que es lo
 * que de verdad no puede duplicarse.
 *
 * ⚠ **El descriptor se compone como TEXTO.** El SVG de la marca no trae «Agentes
 * Aduanales» ni el arco del aniversario (ver `LogoSetieca`): así se selecciona, lo lee
 * un lector de pantalla y se cambia en un sitio.
 *
 * ⚠ **El RIF y la dirección salen de la configuración, nunca escritos aquí.** Un
 * membrete con el RIF a mano es un dato que se queda viejo sin que nadie lo note, y
 * éste va en un papel que se entrega. Lo que no esté configurado sencillamente no se
 * imprime: media línea de dirección es peor que ninguna.
 */

/** Cómo se describe la agencia bajo su nombre. No está en el SVG de la marca. */
const DESCRIPTOR = "Agentes Aduanales"

export function MembreteImpreso({
  empresa,
  alto = 17,
}: {
  empresa: Empresa
  /** Alto de la marca en mm. */
  alto?: number
}) {
  return (
    <header style={{ display: "flex", alignItems: "flex-end", gap: "5mm" }}>
      <LogoSetieca
        style={{ height: `${alto}mm`, width: "auto", color: "var(--impreso-vino)", flexShrink: 0 }}
      />
      <div style={{ minWidth: 0, flex: 1, textAlign: "right" }}>
        <p
          style={{
            margin: 0,
            fontSize: "4.1mm",
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: "0.05mm",
            color: "var(--impreso-tinta)",
            textTransform: "uppercase",
          }}
        >
          {comoSeLlama(empresa)}
        </p>
        <p
          style={{
            margin: "0.6mm 0 0",
            fontSize: "2.9mm",
            letterSpacing: "0.9mm",
            textTransform: "uppercase",
            color: "var(--impreso-vino)",
          }}
        >
          {DESCRIPTOR}
        </p>
        {empresa.rif && (
          <p
            className="tabular"
            style={{ margin: "0.6mm 0 0", fontSize: "2.7mm", color: "var(--impreso-gris)" }}
          >
            RIF {empresa.rif}
          </p>
        )}
      </div>
    </header>
  )
}

/**
 * El filete dorado de la firma corporativa, recto.
 *
 * ⚠ **Es un degradado, no una línea de un solo oro.** Los cuatro tramos son los de
 * `MarcoImpreso` —y los de la firma de los correos—, tendidos a lo largo en vez de
 * siguiendo la onda: un dorado plano al lado de un rótulo del archivo se lee como otro
 * color, no como el mismo material visto de otra manera.
 */
export function FileteOro({ grosor = 0.8 }: { grosor?: number }) {
  return (
    <div
      aria-hidden={true}
      style={{
        height: `${grosor}mm`,
        background:
          "linear-gradient(to right, var(--impreso-oro-1) 0%, var(--impreso-oro-2) 35%, var(--impreso-oro-3) 62%, var(--impreso-oro-4) 100%)",
      }}
    />
  )
}

/**
 * El pie del papel membretado: dónde está la agencia y por dónde se le escribe.
 *
 * ⚠ **Se calla lo que no esté configurado.** Sin dirección ni correo no se imprime ni
 * la banda: un pie con un separador suelto y nada a los lados parece un fallo de
 * impresión, y en un papel que se entrega eso resta más de lo que suma.
 */
export function PieMembrete({ empresa }: { empresa: Empresa }) {
  const partes = [empresa.direccion, empresa.telefono, empresa.email].filter(
    (parte): parte is string => typeof parte === "string" && parte.trim() !== "",
  )
  if (partes.length === 0) return null

  return (
    <footer style={{ textAlign: "center" }}>
      <FileteOro grosor={0.5} />
      <p
        style={{
          margin: "1.6mm 0 0",
          fontSize: "2.5mm",
          lineHeight: 1.35,
          color: "var(--impreso-gris)",
        }}
      >
        {partes.join(" · ")}
      </p>
    </footer>
  )
}
